import moment from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { TimelineEntry, TimelineFieldChange } from "../../../models/EventTimeline";
import {
	GetLabelValues,
	ResolveCompetenceName,
	ResolveRequirementName,
	diffRequirements,
	formatChangeValue,
	formatCompetence,
	formatUserName,
	formatVia,
	getDotClass,
	getEntryLabel,
	getFieldLabel,
	getTimelineContactId,
	isRedactedChange,
	translateEnumValue,
} from "./eventTimeline.utils";

export interface EventTimelineProps {
	/** Timeline entries, newest first, as returned by the history endpoint. */
	entries: TimelineEntry[];
	/**
	 * i18n namespace holding this entity's `event.*` and `fields.*` maps, e.g.
	 * `jobTimeline` or `eventTimeline.consultant`. Shared chrome (System, the
	 * unknown-event fallback, yes/no) always resolves under `eventTimeline`.
	 */
	i18nPrefix: string;
	/** Dot colour per event type. Explicit map so Tailwind's static scan sees the classes. */
	dotClassByType?: { [type: string]: string };
	/** Dot colour per entry source, taking precedence over the type map. */
	dotClassBySource?: { [source: string]: string };
	/** Interpolation values for each entry's label sentence. */
	getLabelValues?: GetLabelValues;
	/** Extra context appended to the byline, after the actor name. */
	renderMeta?: (entry: TimelineEntry) => React.ReactNode;
	/** Extra rows under the byline, above the field-change rows. */
	renderExtraRows?: (entry: TimelineEntry) => React.ReactNode;
	/** The consultant an entry concerns, for the contact context menu. */
	getSubjectContactId?: (entry: TimelineEntry) => string | undefined;
	/**
	 * Resolves requirement keys (certification typeKey / consultant tag key) to
	 * their org-taxonomy display names for `requirements` diff rows. Unresolved
	 * keys render as-is.
	 */
	resolveRequirementName?: ResolveRequirementName;
	/**
	 * Resolves a branch/service pair to the org competence tree's display name
	 * for `competence` change rows. Unresolved pairs render the service key.
	 */
	resolveCompetenceName?: ResolveCompetenceName;
	/** Shown instead of the timeline when there are no entries. */
	emptyState?: React.ReactNode;
	className?: string;
}

type DayGroup = {
	day: string;
	label: string;
	entries: TimelineEntry[];
};

/**
 * Presentational vertical timeline of events ("Händelser") — a dot per event on
 * a shared line, grouped under date headers, newest first.
 *
 * Entity-agnostic: labels resolve through i18n keyed on `entry.type` under the
 * caller's `i18nPrefix`, and everything specific to one entity (dot colours,
 * label interpolation, extra rows) arrives as props. Callers are the per-entity
 * history tabs.
 */
const EventTimeline: React.FC<EventTimelineProps> = ({
	entries,
	i18nPrefix,
	dotClassByType,
	dotClassBySource,
	getLabelValues,
	renderMeta,
	renderExtraRows,
	getSubjectContactId,
	resolveRequirementName,
	resolveCompetenceName,
	emptyState,
	className,
}) => {
	const { t } = useTranslation();

	const groups = useMemo<DayGroup[]>(() => {
		const byDay = new Map<string, DayGroup>();

		for (const entry of entries) {
			const day = moment(entry.date).format("YYYY-MM-DD");

			if (!byDay.has(day))
				byDay.set(day, {
					day,
					label: moment(entry.date).format("D MMMM YYYY"),
					entries: [],
				});

			byDay.get(day)!.entries.push(entry);
		}

		return Array.from(byDay.values());
	}, [entries]);

	if (entries.length === 0)
		return (
			<div className={cn("py-8 text-center text-sm text-gray-500", className)}>
				{emptyState ?? t("eventTimeline.noEvents")}
			</div>
		);

	return (
		<div className={cn("flex flex-col gap-6", className)}>
			{groups.map((group) => (
				<div key={group.day}>
					<h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
						{group.label}
					</h3>

					<ol className="relative ml-1.5 flex flex-col gap-4 border-l border-gray-200 pl-5">
						{group.entries.map((entry) => (
							<EventTimelineRow
								key={entry.id}
								entry={entry}
								i18nPrefix={i18nPrefix}
								dotClassByType={dotClassByType}
								dotClassBySource={dotClassBySource}
								getLabelValues={getLabelValues}
								renderMeta={renderMeta}
								renderExtraRows={renderExtraRows}
								getSubjectContactId={getSubjectContactId}
								resolveRequirementName={resolveRequirementName}
								resolveCompetenceName={resolveCompetenceName}
							/>
						))}
					</ol>
				</div>
			))}
		</div>
	);
};

type RowProps = Pick<
	EventTimelineProps,
	| "i18nPrefix"
	| "dotClassByType"
	| "dotClassBySource"
	| "getLabelValues"
	| "renderMeta"
	| "renderExtraRows"
	| "getSubjectContactId"
	| "resolveRequirementName"
	| "resolveCompetenceName"
> & { entry: TimelineEntry };

const EventTimelineRow: React.FC<RowProps> = ({
	entry,
	i18nPrefix,
	dotClassByType,
	dotClassBySource,
	getLabelValues,
	renderMeta,
	renderExtraRows,
	getSubjectContactId,
	resolveRequirementName,
	resolveCompetenceName,
}) => {
	const { t } = useTranslation();

	const changes = entry.args?.changes ?? [];
	const meta = renderMeta?.(entry);
	const extraRows = renderExtraRows?.(entry);

	// Right-clicking the event line opens the global contact menu for whoever the
	// event concerns; the actor name in the byline opens it for the acting user.
	// Internal users (admins) carry no data-contact → no menu.
	const subjectContactId = getSubjectContactId?.(entry);
	const actorContactId = getTimelineContactId(entry.user);

	return (
		<li className="relative">
			<span
				className={cn(
					"absolute -left-[26px] top-1.5 h-3 w-3 rounded-full ring-4 ring-white",
					getDotClass(entry, dotClassByType, dotClassBySource)
				)}
				aria-hidden
			/>

			<div className="text-sm font-medium text-gray-900" data-contact={subjectContactId}>
				{getEntryLabel(entry, t, i18nPrefix, getLabelValues?.(entry, t))}
			</div>

			<div className="mt-0.5 text-xs text-gray-500">
				{moment(entry.date).format("HH:mm")}
				{" · "}
				<span data-contact={actorContactId}>{formatUserName(entry.user, t)}</span>
				{entry.via && <> {formatVia(entry.via, t)}</>}
				{meta && (
					<>
						{" · "}
						{meta}
					</>
				)}
			</div>

			{extraRows}

			{changes.length > 0 && (
				<ul className="mt-1.5 flex flex-col gap-0.5">
					{changes.map((change, i) => (
						<ChangeRow
							key={`${change.field}-${i}`}
							change={change}
							i18nPrefix={i18nPrefix}
							resolveRequirementName={resolveRequirementName}
							resolveCompetenceName={resolveCompetenceName}
						/>
					))}
				</ul>
			)}
		</li>
	);
};

const ChangeRow: React.FC<{
	change: TimelineFieldChange;
	i18nPrefix: string;
	resolveRequirementName?: ResolveRequirementName;
	resolveCompetenceName?: ResolveCompetenceName;
}> = ({ change, i18nPrefix, resolveRequirementName, resolveCompetenceName }) => {
	const { t } = useTranslation();

	const label = getFieldLabel(change.field, t, i18nPrefix);

	// Sensitive fields say that they changed and nothing more
	if (isRedactedChange(change))
		return (
			<li className="text-xs text-gray-600">
				<span className="font-medium">{label}</span>
				<span className="text-gray-500"> {t("eventTimeline.updated")}</span>
			</li>
		);

	if (change.field === "requirements") {
		const { added, removed } = diffRequirements(change.from, change.to);
		const resolve = (kind: "certification" | "tag", key: string) =>
			resolveRequirementName?.(kind, key) ?? key;

		if (added.length || removed.length)
			return (
				<li className="text-xs text-gray-600">
					<span className="font-medium">{label}</span>
					{": "}
					<span className="inline-flex flex-wrap gap-x-2">
						{added.map(({ kind, key }) => (
							<span key={`+${kind}:${key}`} className="text-emerald-700">
								+ {resolve(kind, key)}
							</span>
						))}
						{removed.map(({ kind, key }) => (
							<span key={`-${kind}:${key}`} className="text-red-700">
								− {resolve(kind, key)}
							</span>
						))}
					</span>
				</li>
			);
	}

	if (change.field === "competence") {
		const from = formatCompetence(change.from, resolveCompetenceName);
		const to = formatCompetence(change.to, resolveCompetenceName);

		if (from !== null || to !== null)
			return (
				<li className="text-xs text-gray-600">
					<span className="font-medium">{label}</span>
					{": "}
					<span className="text-gray-500 line-through decoration-gray-400">{from ?? "–"}</span>
					{" → "}
					<span>{to ?? "–"}</span>
				</li>
			);
	}

	const from = formatChangeValue(translateEnumValue(change.field, change.from, t), t);
	const to = formatChangeValue(translateEnumValue(change.field, change.to, t), t);

	return (
		<li className="text-xs text-gray-600">
			<span className="font-medium">{label}</span>
			{from !== null && to !== null ? (
				<>
					{": "}
					<span className="text-gray-500 line-through decoration-gray-400">{from}</span>
					{" → "}
					<span>{to}</span>
				</>
			) : (
				<span className="text-gray-500"> {t("eventTimeline.changed")}</span>
			)}
		</li>
	);
};

export default EventTimeline;
